import {Form} from '../../../../../stores/Form/Form';
import {RuleType} from '../../../enums';
import {AlertListItem} from '../../../reducer/types';
import {AutomationContextType, AutomationFilter} from '../../../reducer/model';
import {priority} from '@priority-software/priority-proto-api/lib';
import {Dispatch, useCallback} from 'react';
import {ActionTypes, AutomationAction} from '../../../reducer/actions';
import {noop} from '@priority-software/priority-style-react';
import {fetchSysLangs} from '../../../../../Localization/languages';
import {
    getBPMAlertChooses,
    getStatusID,
    getStatusRules,
    handleClosePathRulesDialog,
    openRulesReport
} from '../../../../../_shared/GWTBridge/invoke/bpm/bpmMain';
import {generateAlertList, prepareChooses} from '../utils/oldToNewDataStructure.serializer';
import {bpmOpenRule} from '../api/bpmOpenRule';
import {useAutomationCommonHandlers} from '../../../hooks/useAutomationCommonHandlers';
import {bpmRuleSave} from '../api/bpmRuleSave';
import {prepareListData} from '../../../components/RulesListDialog/parts/AutomationsOverview/utils';
import {bpmToggleActiveRule} from '../api/bpmToggleActiveRule';
import {bpmCopyRule} from '../api/bpmCopyRule';
import {AlertOverviewType} from '../api/types';
import {bpmDeleteRule} from '../api/bpmDeleteRule';
import {bpmChangeRuleOrder} from '../api/bpmChangeRuleOrder';
import {BusinessRuleAction} from '../../../constants';
import {monitorAction} from '../../../../../services/datadog';
import {AIRulesMonitoring} from '../../../components/common/AIAskBlock/enums';
import {getRuleKeyByValue} from '../../../utils';

export type AutomationContextHandlers = {
    handleAutomationOpen: () => void;
    handleDialogType: (type: RuleType) => void;
    openRuleDialog: () => void;
    executeFormAlertReport: (type: RuleType, callback: () => void) => void;
    closeSingleRuleDialog: () => void;
    openAutomationList: (
        params: Partial<{
            showInPopup?: boolean;
            type: RuleType;
            fromStatus?: string;
            toStatus?: string;
            form: Form;
        }>
    ) => Promise<void>;
    removeRule: (ruleNumber: number) => Promise<void>;
    editRule: (ruleNumber: number, fetchHTML?: boolean) => void;
    copyRule: (ruleNumber: number) => Promise<void>;
    handleRuleActive: (
        ruleNumber: number,
        isActive: boolean,
        callback?: () => void
    ) => Promise<void>;
    setFilter: (filter: AutomationFilter) => void;
    setRules: (rules: Array<AlertListItem>) => void;
    removeFilter: (filterName: string, value: string | number | boolean) => void;
    closeAutomation: () => void;
    handleSearch: (searchValue: string) => void;
    handleRuleOrder: (rules: Array<AlertListItem>) => void;
    saveRule: (
        rule: priority.netitems.form.IAlert,
        type: priority.netitems.form.RuleType,
        onSuccess?: () => void
    ) => Promise<void>;
    updateOpenRule: (data: Partial<priority.netitems.form.IAlert>) => void;
    handleFilters: () => void;
    closeCurrentAutomation: () => void;
    setSelectedRuleIndex: (index: number) => void;
    dispatchState: (state: Partial<AutomationContextType>) => void;
};

export const useBPMAutomationContextAPI = (
    dispatch: Dispatch<AutomationAction>,
    state: AutomationContextType
) => {
    const {
        listResponse,
        automationType,
        choosesOptions,
        fromStatus,
        toStatus,
        automationRules,
        aiGeneratedRuleQuery
    } = state;
    const hasAIQuery = !!aiGeneratedRuleQuery;

    const defaultHandlers = useAutomationCommonHandlers(dispatch, state);
    const {handleSearch, setRules, handleAIRuleSuggestions, openRuleDialog} = defaultHandlers;

    const closeCurrentAutomation = useCallback(() => {
        dispatch({type: ActionTypes.CLOSE_AUTOMATION});
        dispatch({type: ActionTypes.DISPATCH_STATE, payload: {fromStatus, toStatus}});
    }, [fromStatus, toStatus]);

    const closeAutomation = useCallback(async () => {
        dispatch({type: ActionTypes.CLOSE_AUTOMATION});
        dispatch({type: ActionTypes.DISPATCH_STATE, payload: {fromStatus, toStatus}});

        // trigger a special indicator at the BPM path, if it has a rule that allows status change
        if (fromStatus && toStatus) {
            handleClosePathRulesDialog(
                fromStatus,
                toStatus,
                automationRules.some(
                    rule => rule.ruleAction === BusinessRuleAction.AllowStatusChange
                )
            );
        }
    }, [fromStatus, toStatus, automationRules]);

    /* CUSTOM API */

    const editRule = useCallback(
        async (ruleNumber: number, fetchHtml?: boolean) => {
            const res = await bpmOpenRule(ruleNumber.toString());

            if (res) {
                const hasData = listResponse.some(rule => rule.AlertNumber === ruleNumber);

                dispatch({
                    type: ActionTypes.UPDATE_LIST_RESPONSE,
                    payload: {
                        data: hasData
                            ? listResponse.map(rule =>
                                  rule.AlertNumber === ruleNumber ? res.alert : rule
                              )
                            : [...listResponse, {...res.alert}]
                    }
                });
                await openRuleDialog(ruleNumber, fetchHtml);
            }
        },
        [listResponse]
    );

    const formatGWTResponse = useCallback(
        (list: AlertOverviewType[]) => {
            const alerts = generateAlertList(list, choosesOptions.MessageTypes);

            return prepareListData(alerts, choosesOptions, automationType);
        },
        [choosesOptions, automationType]
    );

    const openAutomationList = useCallback(
        async ({fromStatus, toStatus}) => {
            const chooses = await getBPMAlertChooses();
            const syslangs = await fetchSysLangs(true);
            const choosesOptions = prepareChooses(chooses, syslangs, fromStatus && toStatus);
            const rules = getStatusRules(toStatus, fromStatus);

            const alerts = generateAlertList(rules, choosesOptions.MessageTypes);
            const mappedData = prepareListData(alerts, choosesOptions, automationType);

            dispatch({
                type: ActionTypes.SET_AUTOMATION_CHOOSES,
                payload: {choosesOptions}
            });

            setRules(mappedData);

            dispatch({type: ActionTypes.OPEN_AUTOMATION});
            dispatch({type: ActionTypes.SET_AUTOMATION_TYPE, payload: {type: RuleType.bpm}});
        },
        [setRules]
    );

    const saveRule = useCallback(
        async (
            rule: priority.netitems.form.IAlert,
            type: priority.netitems.form.RuleType,
            onSuccess?: () => void
        ) => {
            try {
                const res = await bpmRuleSave(rule, fromStatus, toStatus);
                const rules = generateAlertList(res, choosesOptions.MessageTypes);

                if (hasAIQuery) {
                    monitorAction(AIRulesMonitoring.aiRuleSave, {
                        ruleType: getRuleKeyByValue(automationType),
                        query: aiGeneratedRuleQuery
                    });
                }

                if (rules.length > 0) {
                    dispatch({type: ActionTypes.SAVE_RULE, payload: {rules}});
                    dispatch({type: ActionTypes.HANDLE_RULE_FILTERS});
                    onSuccess?.();
                }
            } catch (e) {
                console.error(e);
            }
        },
        [choosesOptions, fromStatus, toStatus]
    );

    const removeRule = useCallback(
        async (ruleNumber: number) => {
            const stringNumber = ruleNumber.toString();
            const res = await bpmDeleteRule(stringNumber, fromStatus, toStatus);

            if (res) {
                dispatch({type: ActionTypes.REMOVE_RULE, payload: {ruleNumber}});
                dispatch({type: ActionTypes.HANDLE_RULE_FILTERS});
            }
        },
        [toStatus, fromStatus]
    );

    const copyRule = useCallback(
        async (copyRuleNumber: number) => {
            const rules = await bpmCopyRule(copyRuleNumber.toString(), fromStatus, toStatus);

            if (rules) {
                const mappedData = formatGWTResponse(rules);
                setRules(mappedData);
                dispatch({type: ActionTypes.HANDLE_RULE_FILTERS});
            }
        },
        [listResponse, automationType, saveRule, setRules, fromStatus, toStatus]
    );

    const handleRuleActive = useCallback(
        async (ruleNumber: number, isActive: boolean, callback: () => void) => {
            const isOk = await bpmToggleActiveRule(
                fromStatus,
                toStatus,
                ruleNumber.toString(),
                isActive
            );
            if (isOk) {
                dispatch({
                    type: ActionTypes.SET_ACTIVE_STATE,
                    payload: {ruleNumber, isActive, rules: listResponse}
                });
                callback();
            }
        },
        [listResponse, automationType, choosesOptions, fromStatus, toStatus]
    );

    const handleRuleOrder = useCallback(
        async (orderedRules: AlertListItem[]) => {
            dispatch({type: ActionTypes.SET_AUTOMATION_RULES, payload: {rules: orderedRules}});
            handleSearch();
            // do server call to update the order
            const orderedIDs = orderedRules.map(rule => ({alert: rule.alertNumber.toString()}));

            await bpmChangeRuleOrder(orderedIDs, fromStatus, toStatus);
        },
        [handleSearch, fromStatus, toStatus]
    );

    const executeFormAlertReport = useCallback(
        async (type: RuleType, callback = noop) => {
            const toStatusId = getStatusID(toStatus);
            const fromStatusId = fromStatus ? getStatusID(fromStatus) : undefined;

            await openRulesReport(fromStatusId, toStatusId);
            callback();
        },
        [toStatus, fromStatus]
    );

    return {
        ...defaultHandlers,
        executeFormAlertReport,
        openAutomationList,
        removeRule,
        editRule,
        copyRule,
        handleRuleActive,
        closeAutomation,
        handleRuleOrder,
        saveRule,
        handleAIRuleSuggestions,
        closeCurrentAutomation
    } as AutomationContextHandlers;
};
